import CnpjUtils from "../../../shared/utils/CnpjUtils";
import MonetaryUtils from "../../../shared/utils/MonetaryUtils";
import PhoneUtils from "../../../shared/utils/PhoneUtils";

// Lista os modos validos da tela de simulacao.
export const ALLOWED_SIMULATION_MODES = ["view", "edit", "create"];

// Verifica se um valor foi preenchido.
export const isFilled = value => {
	return value !== undefined &&
		value !== null &&
		String(value).trim() !== "";
};

// Converte valores monetarios formatados em numero.
export const getCurrencyNumber = value => MonetaryUtils.parseCurrency(value);

// Arredonda valores calculados da simulacao.
export const roundSimulationValue = value => {
	if (value === undefined || value === null || value === "") {
		return "";
	}

	const numericValue = Number(value);

	if (Number.isNaN(numericValue)) {
		return "";
	}

	return Math.round(numericValue);
};

// Verifica se o botao de calcular/recalcular deve ficar desabilitado.
export const isCalculateSubmitDisabled = ({
	values,
	financialInstallmentsLoading,
	financialInstallmentsError
}) => {
	if (financialInstallmentsLoading || financialInstallmentsError) {
		return true;
	}

	return isCalculateButtonDisabled(values);
};

// Valida os campos minimos para habilitar o calculo.
export const isCalculateButtonDisabled = values => {
	const clientDocument = CnpjUtils.cleanAlphanumericCnpj(
		values.clientDocument || ""
	);

	const hasValidCnpj = /^[A-Z0-9]{12}\d{2}$/.test(clientDocument);

	return !(
		hasValidCnpj &&
		isFilled(values.clientName) &&
		isFilled(values.equipmentName) &&
		getCurrencyNumber(values.equipmentValue) > 0 &&
		isFilled(values.paymentConditions)
	);
};

// Calcula entrada minima, entrada maxima e entrada indicada.
export const getInitialPaymentFields = ({
	equipmentValue,
	indicatedConditions,
	simulation
}) => {
	const minimumInitialPercentual =
		indicatedConditions?.minimumInitialPercentual ??
		simulation?.minimumInitialPercentual;

	const maximumInitialPercentual =
		indicatedConditions?.maximumInitialPercentual ??
		simulation?.maximumInitialPercentual;

	const downPaymentPercentage =
		indicatedConditions?.downPaymentPercentage ??
		simulation?.downPaymentPercentage;

	const minimumInitialPayment = MonetaryUtils.calculatePercentageValue(
		equipmentValue,
		minimumInitialPercentual
	);

	const maximumInitialPayment = MonetaryUtils.calculatePercentageValue(
		equipmentValue,
		maximumInitialPercentual
	);

	const initialPayment = MonetaryUtils.calculatePercentageValue(
		equipmentValue,
		downPaymentPercentage
	);

	return {
		minimumInitialPayment: roundSimulationValue(minimumInitialPayment),
		maximumInitialPayment: roundSimulationValue(maximumInitialPayment),
		downPaymentPercentage,
		initialPayment: roundSimulationValue(initialPayment)
	};
};

// Calcula o valor da parcela de recompra.
export const calculateBuybackInstallmentValue = ({
	buybackValue,
	installments
}) => {
	const numericBuybackValue = getCurrencyNumber(buybackValue);
	const numericInstallments = Number(installments);

	if (!numericBuybackValue || !numericInstallments) {
		return "";
	}

	return roundSimulationValue(numericBuybackValue / numericInstallments);
};

// Formata metas Varilux vindas da API para o formato da tela.
export const formatVariluxGoals = metasVarilux => {
	if (!Array.isArray(metasVarilux)) {
		return [];
	}

	return metasVarilux.map((item, index) => ({
		id: item?.ano || index + 1,
		label: item?.ano ? `ANO ${item.ano}` : `ANO ${index + 1}`,
		value: item?.meta ?? ""
	}));
};

// Formata metas de crescimento vindas da API para a tabela da tela.
export const formatGrowthGoals = metasCrescimentoVendas => {
	if (!Array.isArray(metasCrescimentoVendas)) {
		return [];
	}

	return metasCrescimentoVendas.map(item => ({
		growth: `${item?.crescimento || 0}%`,
		grossSalesValue: item?.vendasValorBruto || 0,
		originalInstallmentAmount: item?.valorOriginalParcela || 0,
		netSales: item?.netSales || 0,
		installmentDiscountPercentage: item?.percentualDescontoParcela || 0,
		installmentDiscountValue: item?.valorDescontoParcela || 0,
		totalEquipmentDiscount: item?.descontoTotalEquipamento || 0,
		adjustedInstallmentValue: item?.valorParcelaReajustada || 0,
		adjustedEquipmentValue: item?.valorEquipamentoReajustado || 0
	}));
};

// Formata resposta de calculo/criacao/edicao da simulacao.
export const formatSimulationResponse = simulationData => {
	if (!simulationData) {
		return null;
	}

	const { perfilCliente, metasVarilux, metasCrescimentoVendas } = simulationData;

	return {
		...simulationData,
		id: simulationData.simulacaoId || simulationData.idSimulacao || simulationData.id,
		basedOnMinimumValues: Boolean(perfilCliente?.baseadoValoresMinimos),
		clientNotfound: Boolean(perfilCliente?.clienteNaoEncontrado),
		minimumAverageMonthlySales: perfilCliente?.netSalesMinimo,
		averageMonthlySales: perfilCliente?.mediaVendasMes ?? "",
		grossSalesIndex: perfilCliente?.indiceGrossSalesUtilizado,
		averageGrossSales: perfilCliente?.mediaGrossSalesMes ?? "",
		averageMonthlySalesVarilux: perfilCliente?.mediaVariluxMes ?? "",
		averageMonthlySalesGroup: perfilCliente?.mediaVendasGrupoMes ?? "",
		commercialGroupName: perfilCliente?.nomeGrupoComercial ?? "",
		variluxGoals: formatVariluxGoals(metasVarilux),
		growthGoals: formatGrowthGoals(metasCrescimentoVendas),
		raw: simulationData
	};
};

// Calcula o valor de uma parcela usando parcelaReferencia e desconto.
export const calculateInstallmentValueByReference = ({
	parcelaReferencia,
	desconto
}) => {
	if (
		parcelaReferencia === undefined ||
		parcelaReferencia === null ||
		parcelaReferencia === ""
	) {
		return "";
	}

	const referenceValue = Number(parcelaReferencia);
	const discountValue = Number(desconto || 0);

	if (Number.isNaN(referenceValue) || Number.isNaN(discountValue)) {
		return "";
	}

	const calculatedValue = referenceValue - (referenceValue * discountValue / 100);

	return roundSimulationValue(calculatedValue);
};

// Calcula parcelas a partir da condicao de pagamento indicada.
export const formatCalculatedInstallmentsByPaymentCondition = paymentCondition => {
	const carencia = paymentCondition?.carencia || [];
	const parcelaReferencia = paymentCondition?.parcelaReferencia;

	if (!Array.isArray(carencia) || carencia.length === 0) {
		return [];
	}

	return carencia.map(item => ({
		installment: item?.parcela ?? "",
		calculatedValue: calculateInstallmentValueByReference({
			parcelaReferencia,
			desconto: item?.desconto
		})
	}));
};

// Converte um valor de parcela calculada para numero seguro no payload.
const getCalculatedInstallmentPayloadValue = value => {
	if (typeof value === "number") {
		return value;
	}

	const stringValue = String(value || "").trim();

	if (!stringValue) {
		return 0;
	}

	if (/^\d+(\.\d+)?$/.test(stringValue)) {
		return Number(stringValue);
	}

	return getCurrencyNumber(stringValue);
};

// Monta as parcelas calculadas no formato esperado pelo backend.
export const formatCalculatedInstallmentsPayload = ({
	values = {},
	installments = []
} = {}) => {
	const installmentsFromFields = Object.keys(values || {})
		.filter(key => /^calculatedInstallment\d+$/.test(key))
		.map(key => ({
			parcela: key.replace("calculatedInstallment", ""),
			valorCalculado: values[key]
		}))
		.filter(item => isFilled(item.parcela) && isFilled(item.valorCalculado));

	if (installmentsFromFields.length > 0) {
		return installmentsFromFields;
	}

	if (Array.isArray(values.calculatedInstallments) && values.calculatedInstallments.length > 0) {
		return values.calculatedInstallments.map(item => ({
			parcela: item.parcela ?? item.installment,
			valorCalculado: item.valorCalculado ?? item.calculatedValue
		}));
	}

	return installments.map(item => ({
		parcela: item.parcela ?? item.installment,
		valorCalculado: item.valorCalculado ?? item.calculatedValue
	}));
};

// Monta o payload de criacao/edicao da simulacao.
export const formatSimulationPayload = ({
	values,
	paymentCondition,
	installments = [],
	periodNetRevenue = 0,
	minimumInitialPercentual,
	maximumInitialPercentual,
	buyback = null
}) => {
	const calculatedInstallments = formatCalculatedInstallmentsPayload({
		values,
		installments
	});

	return {
		dadosCliente: {
			cnpj: CnpjUtils.cleanAlphanumericCnpj(values.clientDocument || ""),
			razaoSocial: values.clientName || "",
			faturamentoLiquidoNoPeriodo: getCurrencyNumber(periodNetRevenue)
		},
		nomeEquipamentoSimulado: values.equipmentName || "",
		valorEquipamentos: getCurrencyNumber(values.equipmentValue),
		condicaoComercial: {
			id: Number(paymentCondition?.id ?? values.paymentConditions),
			nome: paymentCondition?.nome || paymentCondition?.label || "",
			carencia: paymentCondition?.carencia || [],
			percentualEntrada: Number(
				paymentCondition?.percentualEntrada ??
				values.downPaymentPercentage ??
				0
			),
			numeroParcelas: Number(
				paymentCondition?.numeroParcelas ??
				values.installments ??
				0
			)
		},
		parcelas: calculatedInstallments.map(item => ({
			parcela: String(item.parcela),
			valorCalculado: getCalculatedInstallmentPayloadValue(item.valorCalculado)
		})),
		percentualEntradaIndicada: {
			minimo: Number(minimumInitialPercentual ?? 0),
			maximo: Number(maximumInitialPercentual ?? 0)
		},
		recompra: buyback
	};
};

// Cria os campos dinamicos de parcelas calculadas para o FormGeneric.
export const createCalculatedInstallmentFields = (
	calculatedInstallments,
	{ emptyValue = MonetaryUtils.formatCurrency(0) } = {}
) => {
	return (calculatedInstallments || []).map((item, index) => ({
		name: `calculatedInstallment${item.installment || index + 1}`,
		label: `Parcela ${item.installment || index + 1}`,
		type: "text",
		defaultValue: item.calculatedValue !== "" &&
			item.calculatedValue !== undefined &&
			item.calculatedValue !== null
			? MonetaryUtils.formatCurrency(item.calculatedValue)
			: emptyValue,
		columns: 2,
		disabled: true
	}));
};

// Converte parcelas do backend para o formato usado pela tela.
export const formatCalculatedInstallments = parcelas => {
	if (!Array.isArray(parcelas)) {
		return [];
	}

	return parcelas.map(item => ({
		installment: item?.parcela ?? "",
		calculatedValue: item?.valorCalculado ?? ""
	}));
};

// Formata resposta da busca de simulacao por ID.
export const formatSimulationByIdResponse = simulationData => {
	if (!simulationData) {
		return null;
	}

	const {
		dadosCliente,
		nomeEquipamentoSimulado,
		valorEquipamentos,
		condicaoComercial,
		parcelas,
		percentualEntradaIndicada,
		recompra,
		perfilCliente,
		metasVarilux,
		metasCrescimentoVendas
	} = simulationData;

	const percentualEntrada =
		percentualEntradaIndicada ??
		simulationData["percentual-entrada-indicada"] ??
		{};

	const hasBuyback = Boolean(
		recompra?.nomeEquipamento ||
		recompra?.valor
	);

	const hasCalculatedInstallments =
		Array.isArray(parcelas) && parcelas.length > 0;

	const canCalculateInstallments =
		condicaoComercial?.parcelaReferencia !== undefined &&
		condicaoComercial?.parcelaReferencia !== null &&
		condicaoComercial?.parcelaReferencia !== "" &&
		Array.isArray(condicaoComercial?.carencia) &&
		condicaoComercial.carencia.length > 0;

	const calculatedInstallments = hasCalculatedInstallments
		? formatCalculatedInstallments(parcelas)
		: canCalculateInstallments
			? formatCalculatedInstallmentsByPaymentCondition(condicaoComercial)
			: [];

	return {
		id: simulationData.simulacaoId || simulationData.idSimulacao || simulationData.id,
		clientDocument: CnpjUtils.formatAlphanumericCnpj(dadosCliente?.cnpj || ""),
		clientName: dadosCliente?.razaoSocial || "",
		equipmentValue: valorEquipamentos ?? "",
		equipmentName: nomeEquipamentoSimulado || "",
		minimumInitialPayment: MonetaryUtils.calculatePercentageValue(
			valorEquipamentos,
			percentualEntrada?.minimo
		),
		maximumInitialPayment: MonetaryUtils.calculatePercentageValue(
			valorEquipamentos,
			percentualEntrada?.maximo
		),
		paymentConditions: condicaoComercial?.id ?? "",
		paymentConditionOptions: condicaoComercial
			? [{
				label: condicaoComercial.nome,
				value: condicaoComercial.id
			}]
			: [],
		installments: condicaoComercial?.numeroParcelas ?? "1",
		maxInstallments: 48,
		downPaymentPercentage: condicaoComercial?.percentualEntrada ?? "",
		initialPayment: MonetaryUtils.calculatePercentageValue(
			valorEquipamentos,
			condicaoComercial?.percentualEntrada
		),
		calculatedInstallments,
		buyback: hasBuyback,
		buybackEquipment: hasBuyback ? recompra?.nomeEquipamento || "" : "",
		buybackValue: hasBuyback ? recompra?.valor ?? "" : "",
		termosCondicoes: simulationData?.termosCondicoes || "",
		basedOnMinimumValues: Boolean(perfilCliente?.baseadoValoresMinimos),
		clientNotfound: Boolean(perfilCliente?.clienteNaoEncontrado),
		minimumAverageMonthlySales: perfilCliente?.netSalesMinimo,
		averageMonthlySales: perfilCliente?.mediaVendasMes || "",
		grossSalesIndex: perfilCliente?.indiceGrossSalesUtilizado,
		averageGrossSales: perfilCliente?.mediaGrossSalesMes ?? "",
		averageMonthlySalesVarilux: perfilCliente?.mediaVariluxMes || "",
		averageMonthlySalesGroup: perfilCliente?.mediaVendasGrupoMes || "",
		commercialGroupName: perfilCliente?.nomeGrupoComercial || "",
		variluxGoals: formatVariluxGoals(metasVarilux),
		growthGoals: formatGrowthGoals(metasCrescimentoVendas),
		raw: simulationData
	};
};

// Monta o payload de envio da proposta ao cliente.
export const formatApplicationPayload = ({
	completeName,
	emails,
	telephone,
	celphone
} = {}) => {
	const formattedEmails = Array.isArray(emails)
		? emails.filter(email => Boolean(email))
		: [];

	return {
		nomeRepresentante: completeName || "",
		emailRepresentante: formattedEmails,
		telefoneRepresentante: PhoneUtils.onlyNumbers(telephone),
		celularRepresentante: PhoneUtils.onlyNumbers(celphone)
	};
};

// Monta a chave de sessionStorage para o modo da simulacao.
const getSimulationModeKey = simulationId => {
	return `simulation-mode-${simulationId || "create"}`;
};

// Salva o modo da simulacao no sessionStorage.
export const saveSimulationMode = ({ simulationId, mode }) => {
	sessionStorage.setItem(getSimulationModeKey(simulationId), mode);
};

// Recupera o modo da simulacao salvo no sessionStorage.
export const getSavedSimulationMode = simulationId => {
	return sessionStorage.getItem(getSimulationModeKey(simulationId));
};

// Remove o modo da simulacao do sessionStorage.
export const clearSimulationMode = simulationId => {
	sessionStorage.removeItem(getSimulationModeKey(simulationId));
};

// Verifica se um valor calculado foi preenchido.
const hasSimulationCalculatedValue = value => {
	return value !== undefined &&
		value !== null &&
		String(value).trim() !== "";
};

// Converte valores calculados para numero, aceitando numero puro e moeda formatada.
const getNumericCalculatedValue = value => {
	if (typeof value === "number") {
		return value;
	}

	const stringValue = String(value ?? "").trim();

	if (!stringValue) {
		return 0;
	}

	if (/^-?\d+(\.\d+)?$/.test(stringValue)) {
		return Number(stringValue);
	}

	return getCurrencyNumber(stringValue);
};

// Converte percentual para numero, aceitando numero puro e texto com %.
const getNumericPercentageValue = value => {
	if (typeof value === "number") {
		return value;
	}

	const stringValue = String(value ?? "")
		.replace("%", "")
		.replace(",", ".")
		.trim();

	if (!stringValue) {
		return 0;
	}

	const numericValue = Number(stringValue);

	return Number.isNaN(numericValue) ? 0 : numericValue;
};

// Verifica se o percentual preenchido deveria gerar um valor calculado maior que zero.
const hasInsufficientCalculatedValue = ({
	percentage,
	calculatedValue
}) => {
	const numericPercentage = getNumericPercentageValue(percentage);

	if (numericPercentage <= 0) {
		return false;
	}

	return getNumericCalculatedValue(calculatedValue) <= 0;
};

// Busca o valor das parcelas em todas as possiveis fontes.
const getCalculatedInstallmentValues = ({
	values = {},
	installments = []
} = {}) => {
	const installmentFields = Object.keys(values || {})
		.filter(key => /^calculatedInstallment\d+$/.test(key))
		.map(key => values[key]);

	if (installmentFields.length > 0) {
		return installmentFields;
	}

	if (Array.isArray(values.calculatedInstallments) && values.calculatedInstallments.length > 0) {
		return values.calculatedInstallments.map(item => {
			return item.calculatedValue ?? item.valorCalculado;
		});
	}

	if (Array.isArray(installments) && installments.length > 0) {
		return installments.map(item => {
			return item.calculatedValue ?? item.valorCalculado;
		});
	}

	return [];
};

// Valida se os valores calculados essenciais existem.
export const validateSimulationCalculationFields = ({
	values = {},
	installments = [],
	minimumInitialPercentual,
	maximumInitialPercentual,
	equipmentValueErrorMessage = "Valor do equipamento insuficiente para o cálculo."
} = {}) => {
	const calculatedInstallmentValues = getCalculatedInstallmentValues({
		values,
		installments
	});

	const hasInvalidCalculatedInstallments =
		calculatedInstallmentValues.some(value => {
			return !hasSimulationCalculatedValue(value);
		});

	const hasMissingCalculatedFields =
		!hasSimulationCalculatedValue(values.minimumInitialPayment) ||
		!hasSimulationCalculatedValue(values.maximumInitialPayment) ||
		!hasSimulationCalculatedValue(values.initialPayment);

	const hasInsufficientMinimumInitialPayment =
		hasInsufficientCalculatedValue({
			percentage: minimumInitialPercentual,
			calculatedValue: values.minimumInitialPayment
		});

	const hasInsufficientMaximumInitialPayment =
		hasInsufficientCalculatedValue({
			percentage: maximumInitialPercentual,
			calculatedValue: values.maximumInitialPayment
		});

	const hasCalculationError =
		hasMissingCalculatedFields ||
		hasInvalidCalculatedInstallments ||
		hasInsufficientMinimumInitialPayment ||
		hasInsufficientMaximumInitialPayment;

	if (!hasCalculationError) {
		return {};
	}

	return {
		equipmentValue: equipmentValueErrorMessage
	};
};